import React, { useContext, useMemo, useCallback } from 'react';
import { BaseCompAction, capitalizeAll, getErrorMessage, SlideOutComp } from '@moderepo/mode-app-components';
import { DialogResponse, DialogResponseCode, DialogType, hideSlideOutPanel, modeUIContext, selectIsEditingForm, showDialog, showErrorNotification,
    showSlideOutPanel, showSuccessNotification, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { makeStyles, Theme } from '@material-ui/core';
import { ritzDataContext, selectLoggedInAgent, selectProjectAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import clsx from 'clsx';
import { ApiError, AlertRule, AlertConditionType } from '@moderepo/mode-apis';
import { Agent, AgentRole, CreateAlertRuleParams } from '@moderepo/mode-internal-apis';
import { deleteAlertRule, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { useRitzTranslation } from '../../utils/customHooks';
import { AlertRuleInfoContainer } from '.';
import { useSelectAlertRule } from '../../utils/dataStateHooks';
import { AlertRulePreviewCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';



const useStyle = makeStyles((theme: Theme) => {
    return {
        // Edit Alert Rule form requires a larger width therefore we need to override the container's width
        [theme.breakpoints.up('md')]: {
            root: {
                '& .slide-out-comp-container.is-editing': {
                    minWidth: 700,
                },
            },
        },

        [theme.breakpoints.up('lg')]: {
            root: {
                '& .slide-out-comp-container.is-editing': {
                    minWidth: 900,
                },
            },
        },

        secondaryAction: {
            '& .icon': {
                color: theme.palette.primary.main,
            },
            '&.delete': {
                '& .icon': {
                    color: theme.palette.secondary.main,
                },
            },
        },
    };
});



interface AlertRulePreviewContainerProps {
    readonly projectId: number;

    // targetAlertRule is either an AlertRule object ID (for Edit Alert Rule mode), or an AlertConditionType value (for CREATE Alert Rule mode)
    // or a clone of an alert rule which is similar to AlertRule object but without some of the attributes
    readonly targetAlertRule: number | AlertConditionType | CreateAlertRuleParams;

    // Whether or not to show the Delete Alert Rule button to allow the user to delete the alert rule. The default is FALSE.
    readonly enableDeleteAlertRule?: boolean | undefined;
}


/**
 * This Component is used for displaying an Alert Rule in the preview panel for preview
 */
export const AlertRulePreviewContainer: React.FC<AlertRulePreviewContainerProps> = (props: AlertRulePreviewContainerProps) => {
    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    // Get the logged in Agent data for the given project. This is is not the same as loggedInAgent data.
    const projectAgent = loggedInAgent ? selectProjectAgent(dataState, project?.id, loggedInAgent.id) : undefined;
    const canEditAlertRule = projectAgent?.role === AgentRole.ADMIN;
    const isEditingForm = selectIsEditingForm(uiState);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();


    // Because props.targetAlertRule can be an ID of an existing Alert Rule or a New Alert Rule type, we need to check to see
    // what it is, existingAlertRuleId or newAlertRuleType, before we can use it.
    const existingAlertRuleId = typeof props.targetAlertRule === 'number' ? props.targetAlertRule : undefined;
    const isNewAlertRule = typeof props.targetAlertRule === 'string' || typeof props.targetAlertRule === 'object';

    // Get the alert rule object for the specified alertRuleId
    const selectedAlertRule = useSelectAlertRule(props.projectId, existingAlertRuleId);


    /**
     * Unselect the selected alert rule
     */
    const unselectAlertRule = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
    }, [uiDispatch]);


    /**
     * On "Create from copy" button clicked, we will copy some of the alert rule's attribute and then dispatch an action to show the
     * Create Alert Rule panel and pass it the copy of the alert rule.
     */
    const onCreateAlertRuleFromCopy = useCallback((alertRule: AlertRule) => {
        // Dispatch an action to show the Preview Panel for the selected alertRule.
        const options: AlertRulePreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.ALERT_RULE_PREVIEW,
            compProps: {
                data: {
                    projectId: props.projectId,

                    // Make a copy of some of the alert rule's settings. Don't copy everything e.g. ID, createdTime, updatedTime, etc...
                    targetAlertRule: {
                        name           : alertRule.name,
                        description    : alertRule.description,
                        enabled        : alertRule.enabled,
                        actions        : JSON.parse(JSON.stringify(alertRule.actions)),
                        condition      : JSON.parse(JSON.stringify(alertRule.condition)),
                        messageTemplate: JSON.parse(JSON.stringify(alertRule.messageTemplate)),
                    },
                },
                enableDeleteAlertRule: true,
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.projectId, uiDispatch]);


    /**
     * onDelete alert rule button clicked
     */
    const onDeleteAlertRule = useCallback((alertRule: AlertRule, errorMessage: string | undefined) => {

        // Confirm before deleting an Entity. Because deleting an alert rule can be dangerous, we will ask the agent to enter the alert rule's name
        uiDispatch(showDialog({
            type          : DialogType.INPUT,
            title         : 'dialog.generic_confirm_delete.title',
            titleData     : { object_name: alertRule.name, object_term: 'alerting.label.alert_rule' },
            message       : 'dialog.confirm_delete_alert_rule.message',
            messageData   : { object_name: alertRule.name },
            positiveButton: 'label.delete',
            negativeButton: 'label.cancel',
            input         : {
                type           : 'text',
                name           : 'label.name',
                error          : errorMessage,
                placeholder    : 'input.delete_object_name.hint_text',
                translationData: {
                    object_term : 'alerting.label.alert_rule',
                    object_field: 'label.name',
                },
                required: true,
            },
            onClose: async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    const alertRuleName = response.input ? response.input.trim() : undefined;

                    if (!alertRuleName) {
                        // Name is not provided. Call this function again to show the delete alert rule dialog with the new error
                        onDeleteAlertRule(alertRule, 'error.input.generic.empty');
                    } else if (alertRuleName !== alertRule.name) {
                        // Name does not match. Call this function again to show the delete alert rule dialog with the new error
                        onDeleteAlertRule(alertRule, 'error.input.generic.invalid');
                    } else {
                        try {
                            // Close the preview panel
                            unselectAlertRule();

                            // Dispatch an action to delete the alert rule
                            await appDataDispatch(deleteAlertRule(props.projectId, alertRule.id));

                            uiDispatch(showSuccessNotification('notif.generic_item_deleted_successfully', {
                                term: capitalizeAll(trans('alerting.label.alert_rule')),
                            }));
                        } catch (error) {
                            // Show error using notification
                            uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));

                            // Call this function again to show the delete alert rule dialog
                            onDeleteAlertRule(alertRule, undefined);
                        }
                    }
                }
            },
        }));
    }, [appDataDispatch, props.projectId, trans, uiDispatch, unselectAlertRule]);


    /**
     * Create an Array of primary actions for the Alert Rule
     */
    const primaryActions = useMemo(() => {
        if (selectedAlertRule && canEditAlertRule) {
            const actions: BaseCompAction[] = [{
                label  : capitalizeAll(trans('alerting.create_alert_rule_copy.label')),
                onClick: () => {
                    onCreateAlertRuleFromCopy(selectedAlertRule);
                },
            }];
            return actions;
        }

        return undefined;
    }, [canEditAlertRule, onCreateAlertRuleFromCopy, selectedAlertRule, trans]);


    /**
     * Create secondary actions for the Alert Rule
     */
    const secondaryActions = useMemo(() => {
        if (selectedAlertRule && props.enableDeleteAlertRule && canEditAlertRule) {
            const actions: BaseCompAction[] = [{
                className: clsx(classes.secondaryAction, 'delete'),
                label    : capitalizeAll(trans('label.delete_object', { term: trans('alerting.label.alert_rule') })),
                icon     : 'delete',
                onClick  : () => {
                    onDeleteAlertRule(selectedAlertRule, undefined);
                },
            }];
            return actions;
        }

        return undefined;
    }, [canEditAlertRule, classes.secondaryAction, onDeleteAlertRule, props.enableDeleteAlertRule, selectedAlertRule, trans]);


    /**
     * Handler for the onCancelEdit and onFinishEdit event from AlertRuleInfoContainer
     */
    const onEditAlertRuleDone = useCallback(() => {
        // If this preview panel is showing CREATE edit rule then if the usee canceled creating a new alert rule, we need to hide the panel
        if (isNewAlertRule) {
            uiDispatch(hideSlideOutPanel());
        }
    }, [isNewAlertRule, uiDispatch]);



    /**
     * Create the content for the slide-out panel. The content will just going to be a Entity
     */
    const slideOutCompContent = useMemo(() => {
        return (
            <AlertRuleInfoContainer
                className="transparent"
                projectId={props.projectId}
                targetAlertRule={props.targetAlertRule}
                onCancelEdit={onEditAlertRuleDone}
                onFinishEdit={onEditAlertRuleDone}
            />
        );
    }, [onEditAlertRuleDone, props.projectId, props.targetAlertRule]);



    return (
        <SlideOutComp
            className={classes.root}
            primaryActions={primaryActions}
            secondaryActions={secondaryActions}
            compContent={slideOutCompContent}
            closable={!isNewAlertRule}
            hideActionBar={isEditingForm}
            contentContainerProps={{
                className: clsx('slide-out-comp-container', isEditingForm && 'is-editing'),
            }}
        />
    );
};
